import { IDataPackageFileDescriptor, IUploader } from '@vivli/shared/features/data-package/infrastructure/interface';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { DropdownFieldComponent } from '@vivli/shared/components';
import moment from 'moment';
import { GridCellRendererEnum } from '@vivli/shared/infrastructure/enum';
import { Color } from '@vivli/shared/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEyeSlash, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ButtonView, GridView } from '@vivli/shared/views';
import { DTIAMRDropDown } from '@vivli/shared/infrastructure/constants';

const fileTypeStyle: CSSProperties = {
    width: '100%',
    marginRight: '10px',
    minWidth: '160px',
    paddingTop: '0px',
    marginTop: '0px',
    padding: '5px',
    backgroundColor: 'transparent'
};

const fileTypeDivStyle: CSSProperties = {
    backgroundColor: 'initial',
    gridTemplateColumns: 'auto',
    boxShadow: 'none'
};

const btnStyle: CSSProperties = {
    padding: 0,
    minWidth: '45px',
    width: '45px'
};

const customContainerStyle: CSSProperties = {
    display: 'grid',
    placeContent: 'center end'
};

const btnContainerStyle = (show2ndCol?: boolean): CSSProperties => ({
    display: 'grid',
    gridTemplateColumns: show2ndCol ? 'auto auto' : 'auto',
    columnGap: '10px'
});

const uploadingContainerStyle: CSSProperties = {
    color: Color.WHITE,
    width: 100,
    height: 30,
    marginRight: 10,
    backgroundColor: Color.VIVLI_LIGHT_BLUE,
    position: 'relative'
};

const uploadingStyle = (progressPercent: number): CSSProperties => ({
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'inline-block',
    width: `${progressPercent}%`,
    height: 30,
    transition: 'width 0.4s ease-in-out',
    backgroundColor: Color.VIVLI_BLUE
});

const uploadingTextStyle: CSSProperties = {
    position: 'absolute',
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
};

const typeDropdownStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'grid',
    alignContent: 'center',
    textAlign: 'left',
    lineHeight: 1
};

interface UploaderListViewProps extends IUploader {
    files?: IDataPackageFileDescriptor[];
    onFileDeleted?: (file: IDataPackageFileDescriptor) => void;
    scrollEnabled?: boolean;
    hybridMode?: boolean;
    secondaryFilesToShow?: IDataPackageFileDescriptor[];
}

export const UploaderListView = (props: UploaderListViewProps) => {
    const {
        files,
        readonly,
        showExpandedFilename,
        isLoading,
        availableTypes,
        hideFileType,
        showUploadDate,
        allowDelete,
        allowOwnerDelete,
        allowDownload,
        onFileDeleted,
        onFileDownload,
        onFileTypeChanged,
        onPubliclyAvailableChanged,
        user,
        allowUpload,
        miniature,
        showPubliclyAvailable,
        rowHeight = 55,
        hideForPubliclyAvailableTypes,
        hideSize,
        allowPubliclyAvailableEdit,
        showOrgName,
        usePagination,
        pageSize,
        hybridMode
    } = props;

    const getDisplaySize = (file: IDataPackageFileDescriptor) => {
        if (!file) {
            return '';
        }

        let { sizeInKb } = file;
        let displaySize = '';

        // 0 is equal to false so specifically checking for null or undefined
        if (sizeInKb === undefined || sizeInKb === null) {
            return displaySize;
        }

        if (sizeInKb > 1024) {
            sizeInKb = sizeInKb / 1024;
            displaySize = `${parseFloat(sizeInKb.toString()).toFixed(2)}MB`;
        } else if (sizeInKb <= 0) {
            displaySize = '< 1 kB';
        } else {
            displaySize = `${parseFloat(sizeInKb.toString()).toFixed(2)}kB`;
        }

        return displaySize;
    };

    const TypeDropdownView = ({ data, node }: ICellRendererParams<IDataPackageFileDescriptor>) => {
        const isLinkedSharedFile = data.publicAccessGranted && !showPubliclyAvailable;

        return (
            <DropdownFieldComponent
                placement="auto"
                style={fileTypeStyle}
                divStyle={fileTypeDivStyle}
                iconStyle={{ bottom: '8' }}
                readonly={readonly || isLoading || isLinkedSharedFile}
                defaultValue={data.type}
                items={availableTypes?.map((t) => ({
                    title: t,
                    value: t
                }))}
                onChange={(newFileType) => {
                    onFileTypeChanged && onFileTypeChanged(data, newFileType as string);

                    // update the grid so it hides / shows publicly available checkboxes as needed
                    node.updateData({
                        ...data,
                        type: newFileType as string
                    });
                }}
                // error={fieldHasError()}
                hidePopoverError={true}
                dataId={DTIAMRDropDown.DropdownMenu('value')}
            />
        );
    };

    const RowControlsView = (file: IDataPackageFileDescriptor) => {
        const isOwner = user?.id === file.uploadedByUserId;
        const showDeleteBtn = allowDelete || (allowOwnerDelete && isOwner);

        return (
            <div style={btnContainerStyle(showDeleteBtn)}>
                {showDeleteBtn && (
                    <ButtonView
                        title={hybridMode && file.publicAccessGranted ? 'Remove File' : 'Delete File'}
                        style={btnStyle}
                        onClick={() => onFileDeleted(file)}
                        theme={'red-on-white'}
                    >
                        <FontAwesomeIcon
                            style={{ cursor: 'pointer', color: Color.ERROR_RED }}
                            icon={hybridMode && file.publicAccessGranted ? faEyeSlash : faTrash}
                        />
                    </ButtonView>
                )}
                {allowDownload && (
                    <ButtonView title="Download File" style={btnStyle} onClick={() => onFileDownload(file)}>
                        <FontAwesomeIcon style={{ cursor: 'pointer', color: 'white' }} icon={faDownload} />
                    </ButtonView>
                )}
            </div>
        );
    };

    const UploadControlsView = (file: IDataPackageFileDescriptor) => {
        return (
            <div style={uploadingContainerStyle}>
                <div style={uploadingStyle(file.percentUploaded)} />
                <div style={uploadingTextStyle}>Uploading</div>
            </div>
        );
    };

    const gridCols: ColDef<IDataPackageFileDescriptor>[] = [
        {
            headerName: 'File Name',
            field: 'name',
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: ({ data, node }) => ({
                dataId: `file-${node.rowIndex}-name-${data.name}`,
                scrollable: false,
                centered: false
            }),
            valueGetter: ({ data }) => {
                const filenameText = showExpandedFilename
                    ? data.name +
                      ' - Uploaded: ' +
                      moment(data.updatedDateTime).format('M-D-YY h:mm a') +
                      ', Uploaded by: ' +
                      data.uploadedByUserEmail
                    : data.name;
                return filenameText;
            },
            minWidth: miniature ? 150 : 300
        },
        {
            headerName: 'Size',
            field: 'sizeInKb',
            valueFormatter: ({ data }) => getDisplaySize(data),
            maxWidth: 100,
            minWidth: 100,
            hide: hideSize || showExpandedFilename,
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: (params: ICellRendererParams) => ({
                component: <div>{params.valueFormatted}</div>,
                dataId: `file-${params.node.rowIndex}-size-${params.valueFormatted}`
            })
        },
        {
            headerName: 'Org Name',
            field: 'organizationName',
            maxWidth: 200,
            minWidth: 100,
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: ({ data, node }) => ({
                dataId: `file-${node.rowIndex}-org-name`
            }),
            hide: !showOrgName
        },
        {
            headerName: 'Uploaded By',
            field: 'uploadedByUserName',
            maxWidth: 200,
            minWidth: 100,
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: ({ data, node }) => ({
                dataId: `file-${node.rowIndex}-uploaded-by-${data.uploadedByUserName}`,
                scrollable: false
            }),
            hide: showExpandedFilename
        },
        {
            headerName: 'File Type',
            field: 'type',
            minWidth: 350,
            maxWidth: 350,
            sortable: false,
            hide: hideFileType || availableTypes?.length === 0 || readonly,
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: (params: ICellRendererParams) => ({
                component: TypeDropdownView(params),
                containerStyle: typeDropdownStyle,
                dataId: `file-${params.node.rowIndex}-type-${params.data.type}`
            })
        },
        {
            headerName: 'File Type',
            field: 'type',
            hide: hideFileType || !readonly,
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: (params: ICellRendererParams) => ({
                component: <div>{params.data.type}</div>,
                dataId: `file-${params.node.rowIndex}-type-${params.data.type}`
            })
        },
        {
            headerName: 'Publicly Available',
            field: 'publicAccessGranted',
            hide: hideFileType || !showPubliclyAvailable,
            maxWidth: 150,
            minWidth: 150,
            filter: false,
            cellRenderer: GridCellRendererEnum.GridCheckbox,
            cellRendererParams: ({ data, node }) => ({
                style: { height: '20px', width: '20px' },
                onChange: (checked: boolean, file: IDataPackageFileDescriptor) => {
                    onPubliclyAvailableChanged(file, checked);
                    return true;
                },
                hide: hideForPubliclyAvailableTypes?.some((type) => type === data.type),
                readonly: readonly && !allowPubliclyAvailableEdit,
                dataId: `file-${node.rowIndex}-publicly-available-${data.publicAccessGranted ? 'true' : 'false'}`
            })
        },
        {
            headerName: 'Uploaded Date',
            field: 'updatedDateTime',
            hide: !showUploadDate,
            minWidth: 150,
            maxWidth: 200,
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: (params: ICellRendererParams) => ({
                component: <div>{params.valueFormatted}</div>,
                dataId: `file-${params.node.rowIndex}-uploaded-date-${params.data.updatedDateTime}`
            }),
            valueFormatter: ({ value }) => moment(value).local().format('M/D/YYYY h:mm:ss a')
        },
        {
            headerName: '',
            hide: !allowDownload && !allowOwnerDelete && !allowDelete && !allowUpload,
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: ({ data }) => {
                const showUploading = !data.isComplete && data.percentUploaded !== undefined;
                return {
                    containerStyle: customContainerStyle,
                    component: showUploading ? UploadControlsView(data) : RowControlsView(data)
                };
            },
            maxWidth: 120,
            minWidth: 120,
            suppressMovable: true,
            sortable: false,
            filter: false,
            floatingFilter: false
        }
    ];

    const grid = (
        <GridView
            rowData={files}
            columns={gridCols}
            hideHeader={false}
            hideFloatingFilter={false}
            fullWidthColumns={true}
            emptyMessage={'No files uploaded.'}
            autoHeight={true}
            rowHeight={rowHeight}
            isLoading={isLoading}
            pagination={usePagination}
            pageSize={pageSize}
            getRowId={(params) => {
                return params.data.name;
            }}
        />
    );

    return <div>{grid}</div>;
};
